import React from 'react';
import Footer from '../../components/layout/Footer';

import HeaderMeta from 'components/headerMeta/HeaderMeta';
import { buildPageBreadcrumb } from 'untils/jsonLdSchemas';
import '../../assets/style/itemStyle/common.scss';

const EmptyLegPolicy = () => {
  return (
    <>
      <HeaderMeta
        type="emptyLegs"
        jsonLd={[buildPageBreadcrumb('Empty Leg Policy', '/emptyLegPolicy')]}
      />
      <div className="privacy-policy-content">
        <p style={{ textAlign: 'center' }}>
          <h1 style={{ textDecoration: 'underline' }}>AIRACER EMPTY LEG BOOKING POLICY</h1>
        </p>

        <h2 id="1-overview">1. Overview</h2>
        <p>
          An Empty Leg flight is a repositioning flight operated by an aircraft that would otherwise
          fly without passengers due to an existing operational, positioning, or subsequent flight
          requirement.
        </p>
        <p>
          Because Empty Leg flights arise from an operator’s pre-existing aircraft schedule, they may
          be offered at a reduced rate compared with standard charter services. However, they are
          also subject to greater uncertainty with respect to timing, routing, airport, aircraft
          availability, and actual operation.
        </p>
        <p>
          All Empty Leg bookings arranged through Airacer are subject to this Policy, together with
          any booking-specific written terms, Booking Confirmation, applicable Charter Agreement,
          operator terms, and other governing documents.
        </p>

        <h2 id="2-airacers-role-and-operational-control">2. Airacer’s Role and Operational Control</h2>
        <p>
          Airacer acts solely as an air charter broker and assists customers in sourcing,
          coordinating, and arranging flights operated by independent third-party air carriers.
        </p>
        <p>
          Airacer does not own, operate, maintain, crew, or exercise operational control over any
          aircraft.
        </p>
        <p>
          The operating carrier retains sole authority and responsibility for all operational
          matters, including aircraft assignment, crew assignment, routing, airport selection,
          safety decisions, weather-related decisions, maintenance, payload limitations, fuel
          planning, and regulatory compliance.
        </p>
        <p>
          All decisions concerning flight safety and actual flight operations are made solely by the
          operating carrier.
        </p>

        <h2 id="3-nature-of-empty-leg-flights">3. Nature of Empty Leg Flights</h2>
        <p>
          Empty Leg flights are opportunistic inventory arising from an aircraft’s existing
          operational schedule and are not on-demand charter flights arranged specifically around the
          Customer’s preferred schedule.
        </p>
        <p>
          The existence of an Empty Leg may depend entirely on another flight, positioning
          requirement, or aircraft movement unrelated to the Customer.
        </p>
        <p>
          If the underlying operational requirement changes, the Empty Leg may also be delayed,
          rescheduled, rerouted, substituted, withdrawn, or canceled entirely.
        </p>
        <p>
          Customers should therefore expect a greater degree of schedule and operational flexibility
          than with a standard charter.
        </p>
        <p>
          Empty Leg flights are not recommended for travel requiring strict schedule certainty,
          including commercial or international connections, medical appointments, major events,
          cruises, or other time-critical travel.
        </p>
        <p>
          If a Customer elects to rely on an Empty Leg for such travel, the Customer assumes the
          associated risk.
        </p>

        <h2 id="4-schedule-routing-airport-and-aircraft-flexibility">
          4. Schedule, Routing, Airport, and Aircraft Flexibility
        </h2>
        <p>
          Because Empty Leg flights are tied to an operator’s operational needs, any of the following
          may change:
        </p>
        <ul>
          <li>departure time</li>
          <li>arrival time</li>
          <li>flight duration</li>
          <li>routing</li>
          <li>departure airport</li>
          <li>arrival airport</li>
          <li>aircraft type</li>
          <li>specific operating aircraft</li>
        </ul>
        <p>
          Changes may result from factors including aircraft positioning requirements, changes to the
          underlying flight, crew duty limitations, maintenance, weather, air traffic control,
          airport restrictions, slot availability, curfews, government requirements, permits,
          customs, security considerations, or other operational reasons.
        </p>
        <p>
          Reasonable schedule adjustments, nearby airport substitutions, and comparable or upgraded
          aircraft substitutions do not automatically constitute a cancellation.
        </p>
        <p>
          Airacer does not guarantee that an Empty Leg will operate exactly as initially displayed,
          quoted, or requested.
        </p>

        <h2 id="5-availability-and-confirmation">5. Availability and Confirmation</h2>
        <p>
          All Empty Leg listings, quotes, aircraft details, and schedule information are dynamic and
          remain subject to prior sale, operator confirmation, aircraft and crew availability,
          operational review, regulatory approval, airport and slot availability, and receipt of
          required payment.
        </p>
        <p>
          No quote, aircraft, itinerary, or schedule shall be considered final until Airacer provides
          written confirmation.
        </p>
        <p>Airacer may withdraw, revise, or amend any Empty Leg offer before written confirmation.</p>

        <h2 id="6-payment-and-pricing">6. Payment and Pricing</h2>
        <p>
          Because Empty Leg flights are generally short-notice, limited, and dynamic inventory,
          Airacer may require immediate payment, full prepayment, wire transfer, credit card
          payment, or other accelerated payment arrangements.
        </p>
        <p>
          An Empty Leg shall not be considered secured until the required payment has been received,
          the operating carrier has confirmed the flight, and Airacer has provided written
          confirmation.
        </p>
        <p>Empty Leg pricing may change at any time before final confirmation.</p>
        <p>
          Unless expressly stated otherwise in writing, quoted pricing may exclude taxes, Federal
          Excise Tax, international taxes, airport fees, FBO fees, ground handling, de-icing, permit
          fees, pet fees, cleaning fees, catering, Wi-Fi or communication charges, overnight crew
          fees, parking charges, or other variable operational costs.
        </p>
        <p>
          The final amount payable shall be the amount stated in the applicable Booking Confirmation
          or other final written confirmation.
        </p>

        <h2 id="7-airacer-credits">7. Airacer Credits</h2>
        <p>Airacer Credits may not be used toward any Empty Leg flight.</p>
        <p>
          Empty Leg flights constitute special, limited, and dynamically priced inventory and are
          priced and paid for separately from the Airacer Credit program.
        </p>
        <p>
          Any prior refund, compensation, account balance, promotional balance, or other form of
          Airacer Credit may not be applied toward the cost of an Empty Leg flight.
        </p>

        <h2 id="8-passenger-timeliness-and-no-show">8. Passenger Timeliness and No-Show</h2>
        <p>
          Empty Leg flights often operate within strict positioning and downstream scheduling
          requirements and may have little or no ability to accommodate passenger delays.
        </p>
        <p>
          The Customer and all passengers must arrive at the designated FBO and be ready for
          departure at the time specified by Airacer or the operating carrier.
        </p>
        <p>
          If passengers are not ready to depart at the required time, the aircraft may be unable to
          wait.
        </p>
        <p>
          Late arrival, failure to appear, incomplete passenger information, passport or visa issues,
          non-compliant baggage, incomplete pet documentation, or Customer-requested delays may be
          treated as a Customer cancellation or no-show, and amounts paid may be non-refundable.
        </p>

        <h2 id="9-passenger-documents-baggage-and-pets">9. Passenger Documents, Baggage, and Pets</h2>
        <p>
          The Customer is responsible for ensuring that all passengers possess all required
          passports, visas, entry permits, transit visas, immigration documents, customs documents,
          health documents, minor travel authorizations, pet documentation, and any other
          documentation required by governmental authorities.
        </p>
        <p>
          Airacer may assist with general operational information but does not guarantee a
          passenger’s eligibility to enter, transit, or remain in any country or jurisdiction.
        </p>
        <p>
          All passenger capacity, baggage allowance, and payload limits remain subject to the actual
          operating limitations of the aircraft and final approval by the operating carrier.
        </p>
        <p>
          Published seating capacity does not guarantee that the same number of passengers may be
          carried under all routing, weather, fuel, runway, or payload conditions.
        </p>
        <p>
          All pets must be disclosed in advance and are subject to operator approval. Any additional
          cleaning, handling, documentation, quarantine, or related charges may be payable by the
          Customer.
        </p>

        <h2 id="10-aircraft-information-and-amenities">10. Aircraft Information and Amenities</h2>
        <p>
          Aircraft images, cabin photographs, configurations, year of manufacture, refurbishment
          information, amenities, and other aircraft details provided by Airacer may be supplied by
          operators or third parties.
        </p>
        <p>
          Unless Airacer expressly confirms a specific aircraft registration in writing, aircraft
          images may be representative only.
        </p>
        <p>The operating aircraft may be substituted for operational reasons.</p>
        <p>
          Wi-Fi, Starlink, streaming, cabin service, flight attendants, galley facilities, sleeping
          configurations, entertainment systems, and other specific amenities are not guaranteed
          unless expressly confirmed in writing.
        </p>

        <h2 id="11-operator-cancellation-rescheduling-or-withdrawal">
          11. Operator Cancellation, Rescheduling, or Withdrawal
        </h2>
        <p>
          An Empty Leg may be canceled, rescheduled, amended, or withdrawn if the underlying
          operational requirement changes.
        </p>
        <p>This may include, without limitation:</p>
        <ul>
          <li>cancellation or modification of the underlying primary flight</li>
          <li>elimination of the positioning requirement</li>
          <li>aircraft reassignment</li>
          <li>aircraft maintenance or AOG events</li>
          <li>crew limitations</li>
          <li>weather</li>
          <li>air traffic control</li>
          <li>slot restrictions</li>
          <li>airport closure</li>
          <li>curfews</li>
          <li>government restrictions</li>
          <li>permit delays</li>
          <li>security issues</li>
          <li>other operational circumstances beyond Airacer’s reasonable control</li>
        </ul>
        <p>
          Airacer will notify the Customer as soon as reasonably practicable after becoming aware of
          such a change.
        </p>

        <h2 id="12-refunds">12. Refunds</h2>
        <p>
          If an Empty Leg flight is canceled, withdrawn, or materially changed due to operator or
          operational reasons, any refund payable to the Customer will be determined based on the
          amounts actually refunded or credited to Airacer by the operating carrier and any applicable
          third-party suppliers.
        </p>
        <p>
          The refundable portion attributable to the operating carrier shall not exceed the amount
          actually refunded or credited by the operating carrier to Airacer.
        </p>
        <p>
          Airacer service fees, handling fees, brokerage fees, payment processing fees, and any other
          fees identified as non-refundable at the time of booking may be retained by Airacer and
          deducted from any amount otherwise refundable to the Customer.
        </p>
        <p>
          Any third-party costs already incurred and not recovered by Airacer, including permit
          fees, airport fees, ground handling charges, catering costs, payment processing fees, or
          other non-recoverable expenses, may also be deducted from the refund.
        </p>
        <p>
          The Customer’s final refund amount will reflect any applicable non-refundable Airacer fees
          and any unrecovered third-party costs in accordance with the applicable booking terms.
        </p>
        <p>
          If the applicable Booking Confirmation, Charter Agreement, or other booking-specific
          written terms contain different or more specific refund provisions, those terms shall
          control.
        </p>

        <h2 id="13-replacement-flights-and-additional-costs">
          13. Replacement Flights and Additional Costs
        </h2>
        <p>
          If an Empty Leg is canceled, withdrawn, or materially changed, Airacer may use reasonable
          efforts to assist the Customer in sourcing an alternative.
        </p>
        <p>
          Airacer does not guarantee the availability of an alternative with the same route,
          timing, airport, aircraft type, aircraft category, or price.
        </p>
        <p>
          Any replacement flight shall be treated as newly available inventory and shall be subject to
          new pricing and operator approval.
        </p>
        <p>
          Unless expressly agreed otherwise by Airacer in writing, any additional cost associated with
          a standard charter or other replacement transportation shall be the responsibility of the
          Customer.
        </p>
        <p>
          Airacer is under no obligation to provide replacement charter service at the original Empty
          Leg price.
        </p>

        <h2 id="14-customer-cancellation">14. Customer Cancellation</h2>
        <p>
          Cancellation by the Customer after confirmation may result in partial or full cancellation
          charges.
        </p>
        <p>
          The applicable amount may depend on the timing of cancellation, operator terms, whether the
          aircraft has been secured or committed, third-party costs already incurred, and other
          non-recoverable costs.
        </p>
        <p>
          Because Empty Legs are special and opportunistic inventory, some bookings may become fully
          non-refundable immediately upon confirmation.
        </p>
        <p>The applicable cancellation terms shall be those communicated for the specific booking.</p>

        <h2 id="15-limitation-of-liability">15. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by applicable law, Airacer shall not be liable for indirect,
          incidental, special, or consequential losses arising from the cancellation, delay,
          rescheduling, modification, withdrawal, or non-operation of an Empty Leg flight.
        </p>
        <p>Such losses may include, without limitation:</p>
        <ul>
          <li>missed commercial or international connections</li>
          <li>hotel costs</li>
          <li>ground transportation costs</li>
          <li>lost business opportunities</li>
          <li>canceled events</li>
          <li>missed weddings, meetings, or cruises</li>
          <li>lost profits</li>
          <li>reputational loss</li>
          <li>other downstream financial losses</li>
        </ul>
        <p>
          Any commercial airfare, ground transportation, hotel accommodation, standard charter, or
          other replacement transportation arranged by the Customer shall generally remain the
          Customer’s responsibility unless Airacer expressly agrees otherwise in writing.
        </p>

        <h2 id="16-force-majeure-and-events-beyond-reasonable-control">
          16. Force Majeure and Events Beyond Reasonable Control
        </h2>
        <p>
          Airacer shall not be liable for cancellation, delay, rescheduling, modification, or other
          performance impacts resulting from events beyond its reasonable control.
        </p>
        <p>
          Such events may include severe weather, natural disasters, air traffic control
          restrictions, airport or runway closures, governmental action, war, terrorism, security
          events, strikes, public health events, travel restrictions, permit delays, customs or
          immigration delays, aircraft maintenance, AOG events, crew illness, crew duty-time
          limitations, or similar operational events that cannot reasonably be predicted or
          controlled.
        </p>

        <h2 id="17-third-party-operator-terms-and-order-of-precedence">
          17. Third-Party Operator Terms and Order of Precedence
        </h2>
        <p>All Empty Leg flights are operated by independent third-party air carriers.</p>
        <p>
          Customers remain subject to the applicable requirements of the operating carrier, including
          safety requirements, passenger manifest deadlines, passport requirements, pet policies,
          baggage limitations, dangerous goods rules, check-in requirements, payment deadlines, and
          operational restrictions.
        </p>
        <p>
          If the operator requires additional documentation or signatures, the Customer must provide
          them in a timely manner.
        </p>
        <p>
          If there is any conflict between this Policy and written terms applicable to a specific
          booking, the applicable Booking Confirmation, Charter Agreement, or other booking-specific
          written terms shall control over this general Policy.
        </p>
        <p>
          The operating carrier’s safety, regulatory, and operational requirements shall control with
          respect to actual flight operations and safety matters.
        </p>

        <h2 id="18-no-waiver-and-customer-acknowledgment">
          18. No Waiver and Customer Acknowledgment
        </h2>
        <p>
          Any refund, credit, exception, waiver, compensation, complimentary service, or other
          special arrangement provided by Airacer in a particular case applies only to that specific
          case and does not create an obligation for Airacer to provide the same treatment in any
          future booking.
        </p>
        <p>
          By making payment, signing or accepting a Booking Confirmation, confirming a booking, or
          traveling on the flight, the Customer acknowledges that the Customer has read, understood,
          and accepted this Policy and all other terms applicable to the specific booking.
        </p>
        <p>
          The Customer further acknowledges that Empty Leg flights involve greater uncertainty than
          standard charter services with respect to schedule, routing, aircraft, and actual
          operation, and voluntarily accepts those risks.
        </p>
      </div>
      <Footer />
    </>
  );
};

export default EmptyLegPolicy;
